import { useContext, useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import message from 'antd/lib/message'

import Forms from 'contexts/Forms'
import { useGetQuickSearchDataForClientDetails } from 'services/clients/clients.service'
import { commonUtils } from 'utils'
import { IClientDetails, IQuickSearch } from 'interfaces/quickSearch.interface'

const saveClientDetails = (clientDetails: IClientDetails) => {
  const newState: IQuickSearch = {
    ...JSON.parse(localStorage.getItem('quickSearch') || '{}'),
    clientDetails,
  }
  localStorage.setItem('quickSearch', JSON.stringify(newState))
}

const hasMedicalDetails = ({ firstLife, secondLife, isSecondLife }: IClientDetails): boolean => {
  const hasSecondLifeMedicalDetails = !!isSecondLife && !!secondLife?.enterMedicalDetails
  return !!firstLife?.enterMedicalDetails || hasSecondLifeMedicalDetails
}

const useClientDetailsTab = () => {
  const {
    quickSearch: { tabs, activeTab, setActiveTab, setTabs },
  } = useContext(Forms)
  const urlParams = new URLSearchParams(window.location.search)
  const idNumber = Number(urlParams.get('idNumber'))
  const { data, loading: initialDataLoading } = useGetQuickSearchDataForClientDetails(idNumber)
  const [loading, setLoading] = useState(initialDataLoading)
  const hasUserEdited = useRef(false)
  const [formData, setFormData] = useState({
    firstLife: {
      title: data?.customerFirst?.title,
      firstName: data?.customerFirst?.firstName,
      lastName: data?.customerFirst?.lastName,
      gender: data?.customerFirst?.gender,
      dateOfBirth: data?.customerFirst?.dateOfBirth,
      useAgeNextBirthday: data?.customerFirst?.useAgeNextBirthday || false,
      enterMedicalDetails: data?.customerFirst?.enterMedicalDetails,
    },
    secondLife: {
      title: data?.customerSecond?.title,
      firstName: data?.customerSecond?.firstName,
      lastName: data?.customerSecond?.lastName,
      gender: data?.customerSecond?.gender,
      dateOfBirth: data?.customerSecond?.dateOfBirth,
      useAgeNextBirthday: data?.customerSecond?.useAgeNextBirthday || false,
      enterMedicalDetails: data?.customerSecond?.enterMedicalDetails,
    },
    isSecondLife: data?.isSecondLife || false,
  })

  const flattenedFormData = commonUtils.flattenObject(formData)

  useEffect(() => {
    window.scroll({ top: 0, behavior: 'smooth' })
  }, [loading])

  useEffect(() => {
    const localData: IClientDetails = JSON.parse(localStorage.getItem('quickSearch') || '{}').clientDetails

    const initialData = {
      firstLife: {
        title: data?.customerFirst?.title || localData?.firstLife?.title,
        firstName: data?.customerFirst?.firstName || localData?.firstLife?.firstName,
        lastName: data?.customerFirst?.lastName || localData?.firstLife?.lastName,
        gender: data?.customerFirst?.gender || localData?.firstLife?.gender,
        dateOfBirth: data?.customerFirst?.dateOfBirth || localData?.firstLife?.dateOfBirth,
        useAgeNextBirthday: data?.customerFirst?.useAgeNextBirthday || localData?.firstLife?.useAgeNextBirthday || false,
        enterMedicalDetails: data?.customerFirst?.enterMedicalDetails || localData?.firstLife?.enterMedicalDetails || false,
      },
      secondLife: {
        title: data?.customerSecond?.title || localData?.secondLife?.title,
        firstName: data?.customerSecond?.firstName || localData?.secondLife?.firstName,
        lastName: data?.customerSecond?.lastName || localData?.secondLife?.lastName,
        gender: data?.customerSecond?.gender || localData?.secondLife?.gender,
        dateOfBirth: data?.customerSecond?.dateOfBirth || localData?.secondLife?.dateOfBirth,
        useAgeNextBirthday: data?.customerSecond?.useAgeNextBirthday || localData?.secondLife?.useAgeNextBirthday || false,
        enterMedicalDetails: data?.customerSecond?.enterMedicalDetails || localData?.secondLife?.enterMedicalDetails || false,
      },
      isSecondLife: localData?.hasOwnProperty('isSecondLife') ? localData.isSecondLife : !!data?.customerSecond,
    }

    setFormData(initialData)
    setLoading(initialDataLoading)
    setMedicalQuestionsTabVisibility(hasMedicalDetails(initialData))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataLoading])

  // Medical question forms read these answers from localStorage, and advisers can open that tab without pressing Next
  useEffect(() => {
    if (!hasUserEdited.current) return
    saveClientDetails(formData)
    setMedicalQuestionsTabVisibility(hasMedicalDetails(formData))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData])

  const handleCheckboxWithLabel = (name: string, e: any) => {
    hasUserEdited.current = true
    setFormData((prevState) => _.set(_.cloneDeep(prevState), name, e.target.checked))
  }

  const handleAntdFormItemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasUserEdited.current = true
    const parsedValue = commonUtils.parseValue(e.target?.value, e.target?.name)
    setFormData((prevState) => _.set(_.cloneDeep(prevState), e.target.name, parsedValue))
  }

  const setMedicalQuestionsTabVisibility = (isVisible: boolean) => {
    setTabs((prevState: Record<string, string>) => {
      const { medicalQuestions, ...restTabs } = prevState
      return isVisible ? { ...restTabs, medicalQuestions: 'MEDICAL QUESTIONS' } : restTabs
    })
  }

  const navigateNext = () => {
    const { clientDetails, medicalQuestions, propertyAndLanding, furtherInformation } = tabs
    const tabValues = [clientDetails, propertyAndLanding, furtherInformation]
    if (medicalQuestions) tabValues.splice(1, 0, medicalQuestions)
    const activeTabIndex = tabValues.indexOf(activeTab)
    setActiveTab(tabValues[activeTabIndex + 1])
  }

  const handleSubmit = async () => {
    saveClientDetails(formData)
    navigateNext()
  }

  const onFinishFailed = (errorInfo: any) => {
    message.error('please fill out every required field')
  }

  return {
    loading,
    flattenedFormData,
    handleCheckboxWithLabel,
    handleAntdFormItemChange,
    handleSubmit,
    onFinishFailed,
  }
}

export default useClientDetailsTab
